// sync.ts — WebSocket client.
//
// Connects to /ws/<roomid>. Receives:
//   - { type: "snapshot", users[], tempo }                (on join)
//   - { type: "clock", bar, beat, tempo, t }               (every beat)
//   - { type: "userJoined", userId, instrument }
//   - { type: "userLeft", userId }
//   - { type: "trackUpdate", userId, activeSource, customPatterns? }
//
// Sends:
//   - { type: "hello", userId, instrument }
//   - { type: "trackUpdate", activeSource, customPatterns? }

import type { InstrumentId } from "./audio";
import type { Loop } from "./loopbank";

export type SyncEvent =
  | { type: "snapshot"; tempo: number; users: Array<{ userId: string; instrument: InstrumentId; activeSource: string | null; customPatterns: Loop[]; voice?: string; name?: string }>; beat?: { on: boolean; groove: string } }
  | { type: "clock"; bar: number; beat: number; tempo: number; t: number }
  | { type: "userJoined"; userId: string; instrument: InstrumentId; voice?: string; name?: string }
  | { type: "userLeft"; userId: string }
  | { type: "trackUpdate"; userId: string; activeSource: string | null; customPatterns?: Loop[] }
  | { type: "plink"; userId: string; x: number; y: number; note: string; voice?: string }
  | { type: "presence"; userId: string; voice?: string; name?: string }
  | { type: "beat"; on: boolean; groove: string };

type Listener = (e: SyncEvent) => void;

export interface SyncClient {
  send(msg: Record<string, unknown>): void;
  on(fn: Listener): () => void;
  close(): void;
  /** "connecting" | "open" | "closed" */
  state(): WebSocketState;
}

type WebSocketState = "connecting" | "open" | "closed";

export function connectRoom(roomId: string, me: { userId: string; instrument: InstrumentId; voice?: string; name?: string }): SyncClient {
  const listeners = new Set<Listener>();
  let ws: WebSocket | null = null;
  let stateVal: WebSocketState = "connecting";
  let reconnectAttempt = 0;
  let closedByUser = false;

  function open() {
    const proto = location.protocol === "https:" ? "wss:" : "ws:";
    const url = `${proto}//${location.host}/ws/${encodeURIComponent(roomId)}`;
    ws = new WebSocket(url);
    stateVal = "connecting";
    ws.addEventListener("open", () => {
      stateVal = "open";
      reconnectAttempt = 0;
      ws?.send(JSON.stringify({ type: "hello", userId: me.userId, instrument: me.instrument, voice: me.voice, name: me.name }));
    });
    ws.addEventListener("close", () => {
      stateVal = "closed";
      if (closedByUser) return;
      reconnectAttempt++;
      const delay = Math.min(5000, 250 * Math.pow(2, reconnectAttempt));
      setTimeout(open, delay);
    });
    ws.addEventListener("message", (ev) => {
      try {
        const data = JSON.parse(ev.data) as SyncEvent;
        for (const l of listeners) l(data);
      } catch {
        // ignore malformed
      }
    });
    ws.addEventListener("error", () => {
      // close handler will trigger reconnect
    });
  }

  open();

  return {
    send(msg) {
      if (ws && stateVal === "open") {
        ws.send(JSON.stringify(msg));
      }
    },
    on(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    close() {
      closedByUser = true;
      ws?.close();
    },
    state() {
      return stateVal;
    },
  };
}

/** Generate a stable per-session user id. Survives tab reloads via sessionStorage. */
export function getOrCreateUserId(): string {
  const key = "dlam-user-id";
  let id = sessionStorage.getItem(key);
  if (!id) {
    id = `u${Math.random().toString(36).slice(2, 8)}`;
    sessionStorage.setItem(key, id);
  }
  return id;
}
